// analytics.ts - Service for fetching usage analytics data

import { UserUsageAnalytics, OverallUsageMetrics } from '../types/analytics';

const API_URL = import.meta.env.VITE_WORKER_URL;

/**
 * Fetches detailed usage analytics for the current user
 */
export async function fetchUserUsageAnalytics(days: number = 30): Promise<UserUsageAnalytics> {
  const token = localStorage.getItem('sessionToken');
  if (!token) {
    throw new Error('No authentication token found');
  }

  // Get current user ID from token (stored in localStorage)
  const userId = localStorage.getItem('userId');
  if (!userId) {
    throw new Error('User ID not found in localStorage');
  }

  // URL-encode the userId to handle special characters like +, /, and =
  const encodedUserId = encodeURIComponent(userId);
  const url = `${API_URL}/admin/analytics/users/${encodedUserId}/usage?days=${days}`;
  console.log('Fetching analytics from:', url);
  console.log('User ID:', userId);
  console.log('Encoded User ID:', encodedUserId);
  console.log('API_URL:', API_URL);

  try {
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      
      if (response.status === 401) {
        throw new Error('Authentication required');
      }
      if (response.status === 403) {
        throw new Error('Access denied - Admin privileges required');
      }
      if (response.status === 404) {
        throw new Error(`User not found: ${userId}`);
      }
      throw new Error(`Failed to fetch analytics (${response.status}): ${response.statusText || errorText}`);
    }

    return response.json();
  } catch (error) {
    if (error instanceof Error) {
      console.error('Fetch error:', error.message);
      throw error;
    }
    throw new Error(`Network error: ${String(error)}`);
  }
}

/**
 * Fetches overall usage metrics (admin only)
 */
export async function fetchOverallUsageMetrics(days: number = 7): Promise<OverallUsageMetrics> {
  const token = localStorage.getItem('sessionToken');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(
    `${API_URL}/admin/analytics/usage?days=${days}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication required');
    }
    if (response.status === 403) {
      throw new Error('Admin access required');
    }
    throw new Error(`Failed to fetch overall metrics: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetches token consumption trends (admin only)
 */
export async function fetchTokenAnalytics(days: number = 7): Promise<import('../types/analytics').TokenAnalytics> {
  const token = localStorage.getItem('sessionToken');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(
    `${API_URL}/admin/analytics/tokens?days=${days}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication required');
    }
    if (response.status === 403) {
      throw new Error('Admin access required');
    }
    throw new Error(`Failed to fetch token analytics: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetches abuse pattern analytics (admin only)
 */
export async function fetchAbuseAnalytics(days: number = 7): Promise<import('../types/analytics').AbuseAnalytics> {
  const token = localStorage.getItem('sessionToken');
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(
    `${API_URL}/admin/analytics/abuse?days=${days}`,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication required');
    }
    if (response.status === 403) {
      throw new Error('Admin access required');
    }
    throw new Error(`Failed to fetch abuse analytics: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Formats a number with commas for readability
 */
export function formatNumber(num: number): string {
  return num.toLocaleString();
}

/**
 * Formats bytes into human-readable format
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Formats a date string into a readable format
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Formats a timestamp into relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  return formatDate(timestamp);
}

/**
 * Formats seconds into human-readable duration
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return remainingSeconds > 0 
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Calculates percentage and formats it
 */
export function formatPercentage(value: number, total: number): string {
  if (total === 0) return '0%';
  const percentage = (value / total) * 100;
  return `${Math.round(percentage)}%`;
}

/**
 * Gets color class based on percentage (for progress bars)
 */
export function getPercentageColor(percentage: number): string {
  if (percentage >= 90) return 'bg-red-500';
  if (percentage >= 75) return 'bg-yellow-500';
  if (percentage >= 50) return 'bg-blue-500';
  return 'bg-emerald-500';
}

/**
 * Gets text color class based on percentage
 */
export function getPercentageTextColor(percentage: number): string {
  if (percentage >= 90) return 'text-red-400';
  if (percentage >= 75) return 'text-yellow-400';
  if (percentage >= 50) return 'text-blue-400';
  return 'text-emerald-400';
}
